import { useQuery } from "@tanstack/react-query";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import {
  type FormattedLineData,
  type PerformanceWithAccountFilter,
  type QueryFilterWithAccount,
  completes,
  lineChartFactory,
  starts,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import * as contentReportingService from "../services/contentReportingService";
import * as dashboardReportingService from "../services/dashboardReportingService";
import { type AccountType } from "../../../interfaces/Account";

export const fetchEmailEngagementQuery = async ({ queryKey }: QueryFilterWithAccount) => {
  const { data } = await contentReportingService.getEmailEngagement(queryKey[1]);

  const barData = [
    {
      id: "sends",
      category: "Sends",
      value: data.Sends ?? 0,
      fillColor: reportingColors.blue,
    },
    {
      id: "opens",
      category: "Opens",
      value: data.Opens ?? 0,
      fillColor: reportingColors.greenDark,
    },
    {
      id: "clicks",
      category: "Clicks",
      value: data.Clicks ?? 0,
      fillColor: reportingColors.yellow,
    },
  ];

  const cardData: CardReportingItem[] = [
    {
      statDescription: "Avg. Send Size",
      stat: data.AvgSendSize,
      popupBody: "The average number of recipients per email send.",
    },
    {
      statDescription: "Open Rate",
      stat: data.OpenRate * 100,
      isStatPercentage: true,
      popupBody: "The percentage of emails opened compared to the sum of how often emails have been sent.",
    },
    {
      statDescription: "Click-to-open Rate",
      stat: data.ClickToOpen * 100,
      isStatPercentage: true,
      popupBody: "The percentage of emails clicked compared to the sum of how often emails have been opened.",
    },
    {
      statDescription: "Click-Through Rate",
      stat: data.ClickThroughRate * 100,
      isStatPercentage: true,
      popupBody: "The percentage of emails clicked compared to the sum of how often emails have been sent.",
    },
  ];

  return {
    raw: data,
    barData,
    cardData,
  };
};

export type emailEngagementQueryProps = {
  filter: PerformanceWithAccountFilter & { type: AccountType };
  enabled: boolean;
};

export const useEmailEngagementQuery = (props: emailEngagementQueryProps) =>
  useQuery({
    queryKey: ["EmailsReportEngagement", props.filter],
    queryFn: fetchEmailEngagementQuery,
    enabled: props.enabled,
  });

export const fetchFlowGoalsLineQuery = async (): Promise<{
  [key in dashboardReportingService.ObjectiveTypes]: FormattedLineData<dashboardReportingService.FlowGoalLineChart>;
}> => {
  const [onboardingTransitionResponse, remediationResponse, optimizationResponse] = await Promise.all([
    dashboardReportingService.fetchFlowGoalsLineData(dashboardReportingService.ObjectiveTypesEnum.OnboardingTransition),
    dashboardReportingService.fetchFlowGoalsLineData(dashboardReportingService.ObjectiveTypesEnum.Remediation),
    dashboardReportingService.fetchFlowGoalsLineData(dashboardReportingService.ObjectiveTypesEnum.Optimization),
  ]);

  return {
    "onboarding/transition": lineChartFactory(onboardingTransitionResponse.data),
    remediation: lineChartFactory(remediationResponse.data),
    optimization: lineChartFactory(optimizationResponse.data),
  };
};

const getBarData = (response: dashboardReportingService.FlowGoalEngagement): DataPoint[] => {
  return [
    {
      id: starts.toLowerCase(),
      category: starts,
      value: response.InProgress,
      fillColor: reportingColors.blue,
    },
    {
      id: completes.toLowerCase(),
      category: completes,
      value: response.Completed,
      fillColor: reportingColors.yellow,
    },
  ];
};

export const fetchFlowGoalsEngagementQuery = async (): Promise<{
  barData: { [key in dashboardReportingService.ObjectiveTypes]: DataPoint[] };
  cardData: CardReportingItem[];
}> => {
  const [onboardingTransitionResponse, remediationResponse, optimizationResponse] = await Promise.all([
    dashboardReportingService.fetchFlowGoalsEngagement(
      dashboardReportingService.ObjectiveTypesEnum.OnboardingTransition,
    ),
    dashboardReportingService.fetchFlowGoalsEngagement(dashboardReportingService.ObjectiveTypesEnum.Remediation),
    dashboardReportingService.fetchFlowGoalsEngagement(dashboardReportingService.ObjectiveTypesEnum.Optimization),
  ]);

  const barData = {
    "onboarding/transition": getBarData(onboardingTransitionResponse.data),
    remediation: getBarData(remediationResponse.data),
    optimization: getBarData(optimizationResponse.data),
  };

  const cardData: CardReportingItem[] = [
    {
      statDescription: "Onboarding / Transition",
      popupBody: "Total number of Flows with the objective set to Onboarding / Transition.",
      stat: onboardingTransitionResponse.data.FlowsCount,
    },
    {
      statDescription: "Remediation",
      popupBody: "Total number of Flows with the objective set to Remediation.",
      stat: remediationResponse.data.FlowsCount,
    },
    {
      statDescription: "Optimization",
      popupBody: "Total number of Flows with the objective set to Optimization.",
      stat: optimizationResponse.data.FlowsCount,
    },
  ];

  return {
    barData,
    cardData,
  };
};

export const useFlowGoalsLineChartQuery = (enabled = true) =>
  useQuery({
    queryKey: ["flowGoals"],
    queryFn: fetchFlowGoalsLineQuery,
    enabled,
  });

export const useFlowGoalsEngagementQuery = (enabled = true) =>
  useQuery({
    queryKey: ["flowGoalsEngagement"],
    queryFn: fetchFlowGoalsEngagementQuery,
    enabled,
  });
